import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { format, add, parse } from 'date-fns';
import {
  Backdrop,
  Button,
  ClickAwayListener,
  Grow,
  IconButton,
  Paper,
  Popper,
  Box,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { DismissIcon } from '@wavetrak/theme';
import { trackEvent, getWindow } from '@surfline/web-common';
import { useRouter } from 'next/router';

import DatePickerCalendar, {
  HISTORIC_DATE_FORMAT,
  HISTORIC_DISPLAY_DATE_FORMAT,
} from 'components/DatePickerCalendar';
import CalendarIcon from 'components/Icons/CalendarIcon';
import { useMinWidthMobileLarge } from 'hooks/useMediaQueries';
import PremiumPill from 'components/Icons/PremiumPill';
import { CLICKED_SUBSCRIBE_CTA } from '../../common/constants';

import styles from './DatePickerMenu.module.scss';

interface Props {
  closeButtonTooltipText?: string;
  showCloseButton?: boolean;
  isPermitted: boolean;
  maxDate: Date;
  minDate: Date;
  range: number;
  setStartDate: (date: Date | null) => void;
  startDate: string | undefined;
  segmentPropertiesCTA: {
    location: string;
    category: string;
    pageName: string;
    spotId: string;
    spotName: string;
  };
  submitButtonText: string;
  selectButtonText: string;
  onClickClearButton?: () => void;
  enableFuture?: boolean;
}

type MenuContentProps = {
  toggleStartDate: (date: Date | null) => void;
  handleClose: () => void;
  handleSetStartDate: () => void;
  isDesktop: boolean;
  maxDate: Date;
  minDate: Date;
  range: number;
  submitButtonText: string;
  startDate: string | undefined;
  enableFuture: boolean;
};

const EndIcon = ({ open }: { open: boolean }) => (
  <span className={styles.endIcon}>{open ? <ExpandLess /> : <ExpandMore />}</span>
);

const DatePickerMenuContent: React.FC<MenuContentProps> = ({
  toggleStartDate,
  handleClose,
  handleSetStartDate,
  isDesktop,
  maxDate,
  minDate,
  range,
  submitButtonText,
  startDate,
  enableFuture = false,
}) => (
  <Box
    aria-labelledby="date-picker-menu-content"
    className={isDesktop ? styles.datePickerMenuContent : styles.datePickerMenuMobile}
    data-testid="date-picker-menu-content"
    id="date-picker-menu-content"
  >
    <Box
      className={isDesktop ? styles.datePickerMenuItem : styles.datePickerMenuItemMobile}
      data-testid="date-picker-menu-item"
      title="Date Picker Menu"
    >
      {!isDesktop && (
        <Box className={styles.datePickerMenuHeader} data-testid="date-picker-menu-header">
          <Typography variant="headline" className={styles.datePickerMenuHeaderTitle}>
            Date selection
          </Typography>
          <IconButton
            className={styles.datePickerHeaderCloseButton}
            data-testid="fullscreen-date-picker-menu-close"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={handleClose}
            size="small"
          >
            <DismissIcon />
          </IconButton>
        </Box>
      )}
      <DatePickerCalendar
        getStartDate={toggleStartDate}
        startDate={startDate}
        maxDate={maxDate}
        minDate={minDate}
        range={range}
        enableFuture={enableFuture}
      />
      <Box
        className={classNames(
          styles.datePickerMenuButtons,
          !isDesktop && styles.datePickerMenuButtonsMobile,
        )}
      >
        <Button
          color="secondary"
          disableRipple
          disableTouchRipple
          data-testid="date-picker-menu-cancel"
          fullWidth={false}
          onClick={handleClose}
          variant="text"
        >
          Cancel
        </Button>
        <Button
          color="secondary"
          disableRipple
          disableTouchRipple
          data-testid="date-picker-menu-submit"
          fullWidth={false}
          onClick={handleSetStartDate}
          variant="text"
        >
          {submitButtonText}
        </Button>
      </Box>
    </Box>
  </Box>
);

const DatePickerMenu = ({
  closeButtonTooltipText,
  showCloseButton = true,
  setStartDate,
  startDate,
  isPermitted,
  maxDate,
  minDate,
  range,
  segmentPropertiesCTA,
  submitButtonText,
  selectButtonText,
  onClickClearButton,
  enableFuture = false,
}: Props) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const isDesktop = useMinWidthMobileLarge();
  const showMobileCalendar = !isDesktop && open;
  const router = useRouter();
  const isNative = router?.query?.native?.toString() === 'true';
  const upgradeUrl = isNative ? 'surfline://upgrade' : '/upgrade';

  const handleToggle = () => {
    if (isPermitted) {
      return setOpen((prevOpen) => !prevOpen);
    }
    trackEvent(CLICKED_SUBSCRIBE_CTA, {
      ...segmentPropertiesCTA,
    });
    getWindow()?.location.assign(upgradeUrl);
    return null;
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);

  const toggleStartDate = (date: Date | null) => {
    if (isPermitted) setSelectedStartDate(date);
  };

  const handleClose = useCallback(() => {
    if (isPermitted) {
      setSelectedStartDate(null);
      return setOpen(false);
    }
    return null;
  }, [isPermitted]);

  const handleSetStartDate = () => {
    setStartDate(selectedStartDate);
    setOpen(false);
  };

  const dateInputText = useMemo(() => {
    if (selectedStartDate) {
      const endDate = add(selectedStartDate, { days: range - 1 });
      const formattedStartDate = format(selectedStartDate, HISTORIC_DISPLAY_DATE_FORMAT);
      const formattedEndDate = format(endDate, HISTORIC_DISPLAY_DATE_FORMAT);

      return `${formattedStartDate} - ${formattedEndDate}`;
    }

    if (startDate) {
      const date = parse(startDate, HISTORIC_DATE_FORMAT, new Date());
      const endDate = add(date, { days: range - 1 });
      const formattedStartDate = format(date, HISTORIC_DISPLAY_DATE_FORMAT);
      const formattedEndDate = format(endDate, HISTORIC_DISPLAY_DATE_FORMAT);

      return `${formattedStartDate} - ${formattedEndDate}`;
    }

    return selectButtonText;
  }, [range, selectButtonText, selectedStartDate, startDate]);

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (!startDate) setSelectedStartDate(null);
  }, [startDate]);

  return (
    <Box className={styles.datePickerMenuWrapper} data-testid="date-picker-menu-wrapper">
      <Backdrop
        className={styles.datePickerBackdrop}
        data-testid="date-picker-backdrop"
        open={open}
      />

      <Button
        aria-controls={open ? 'date-picker-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        className={styles.datePickerDateButton}
        data-testid="date-picker-date-button"
        disableElevation
        disableFocusRipple
        disableRipple
        disableTouchRipple
        endIcon={<EndIcon open={open} />}
        id="date-picker-date-button"
        onClick={handleToggle}
        ref={anchorRef}
        size={isDesktop ? 'large' : 'medium'}
        startIcon={<CalendarIcon />}
        variant="secondary"
      >
        <Box className={styles.datePickerDateButtonContent}>
          <Typography
            className={styles.datePickerDateButtonText}
            variant={isDesktop ? 'subHeadline' : 'callout2'}
          >
            {dateInputText}
          </Typography>
          {!isPermitted && <PremiumPill />}
        </Box>
      </Button>
      {showCloseButton && closeButtonTooltipText && (
        <Tooltip
          arrow
          enterTouchDelay={0}
          placement="top"
          title={closeButtonTooltipText}
          disableTouchListener
        >
          <IconButton
            className={styles.datePickerMenuCloseButton}
            data-testid="date-picker-menu-close-button"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={onClickClearButton}
          >
            <DismissIcon />
          </IconButton>
        </Tooltip>
      )}
      {isPermitted && showMobileCalendar && (
        <DatePickerMenuContent
          toggleStartDate={toggleStartDate}
          handleClose={handleClose}
          handleSetStartDate={handleSetStartDate}
          isDesktop={isDesktop}
          maxDate={maxDate}
          minDate={minDate}
          range={range}
          submitButtonText={submitButtonText}
          startDate={startDate}
          enableFuture={enableFuture}
        />
      )}
      {isPermitted && !showMobileCalendar && (
        <Popper
          anchorEl={anchorRef.current}
          className={styles.datePickerPopper}
          data-testid="date-picker-popper"
          disablePortal
          open={open}
          placement="bottom"
          transition
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Paper className={styles.datePickerMenu}>
                <span
                  className="date-picker-popover-arrow"
                  data-testid="date-picker-popover-arrow"
                />
                <ClickAwayListener onClickAway={handleClose}>
                  <Box>
                    <DatePickerMenuContent
                      toggleStartDate={toggleStartDate}
                      handleClose={handleClose}
                      handleSetStartDate={handleSetStartDate}
                      isDesktop={isDesktop}
                      maxDate={maxDate}
                      minDate={minDate}
                      range={range}
                      submitButtonText={submitButtonText}
                      startDate={startDate}
                      enableFuture={enableFuture}
                    />
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </Box>
  );
};

export default DatePickerMenu;
